import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSectionHeading from "views/system-design/DSSectionHeading";
import DSTypography from "views/system-design/DSTypography";
import { sectionCardSx } from "views/system-design/sectionCardSx";
import { ClockConfigValue } from "../../../api/clockConfig";
import { extractApiErrorMessage } from "../../../utils/util";
import {
  useClockGlobalConfig,
  useUpdateClockGlobalConfig,
} from "../hooks/useClockConfig";
import ClockConfigForm from "./ClockConfigForm";

const formatTime = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";

/**
 * The fleet-wide document. Every clock without an override runs exactly these values,
 * so a save here reaches ~5k devices at their next fetch.
 */
const GlobalConfigTab = () => {
  const { data: config, isLoading, isError, error } = useClockGlobalConfig();
  const { mutate: save, isPending: isSaving } = useUpdateClockGlobalConfig();
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = (values: Record<string, ClockConfigValue>) => {
    setSaveError(null);
    save(values, {
      onError: (e) =>
        setSaveError(
          extractApiErrorMessage(e, "Failed to save the configuration"),
        ),
    });
  };

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (isError) {
    return (
      <DSBox sx={{ p: 2 }}>
        <DSTypography color="error">
          Failed to load the configuration:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSCard sx={[sectionCardSx, { mb: 2 }]}>
      <DSSectionHeading>Global configuration</DSSectionHeading>
      <DSCardContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {config ? (
            <DSTypography variant="caption" color="text.secondary">
              Version {config.version} · saved {formatTime(config.updatedAt)}
              {config.updatedBy ? ` by ${config.updatedBy}` : ""}
            </DSTypography>
          ) : (
            <DSTypography variant="caption" color="text.secondary">
              Nothing published yet — every clock is running the defaults
              compiled into its build.
            </DSTypography>
          )}

          <DSTypography variant="caption" color="text.secondary">
            Removing a key is the rollback path: clocks fall back to the default
            their build ships with. Values are clamped to a safe range on the
            device, so a key it does not recognise changes nothing until a build
            that reads it ships.
          </DSTypography>

          <ClockConfigForm
            values={config?.values ?? {}}
            saving={isSaving}
            error={saveError}
            saveLabel="Save for the whole fleet"
            onSave={handleSave}
          />
        </DSBox>
      </DSCardContent>
    </DSCard>
  );
};

export default GlobalConfigTab;
